import { useClient, useQuery } from '@weenat/client'
import { canUser, ops } from '@weenat/client/dist/core/access'
import {
  SelectableParams,
  SwitchUnivAggParams,
  SwitchUnivMetricParams,
  SwitchUniverseParams
} from '@weenat/client/dist/core/analytics'
import { Universe } from '@weenat/client/dist/core/universes'
import { AddableWeedgetKey, WEEDGET_CATEGORY } from '@weenat/client/dist/resources/weedgetPosition'
import { useOrgContext } from 'app/orgProvider'
import { useLoginAs, useToken } from 'app/state'
import { useUserContext } from 'app/userProvider'
import {
  EventParams,
  logEvent as firebaseLogEvent,
  getAnalytics,
  setUserProperties
} from 'firebase/analytics'
import isNil from 'lodash-es/isNil'
import { useEffect } from 'react'

const logEvent = (eventName: string, params?: EventParams) => {
  const analytics = getAnalytics()

  firebaseLogEvent(analytics, eventName, params)
}

const logEventFb = (
  eventName: string,
  params?: Record<string, unknown>,
  type: FbqTrackingType = 'trackCustom'
) => {
  if (!isNil(fbq) && typeof fbq === 'function') {
    fbq(type, eventName, params)
  }
}

const logAll = (eventName: string, params?: Record<string, unknown>) => {
  logEvent(eventName, params)
  logEventFb(eventName, params)
}

export const useInitTracking = () => {
  const client = useClient()
  const [token] = useToken()
  const [loginAs] = useLoginAs()
  const { me } = useUserContext()
  const org = useOrgContext()

  const myInfoRequest = useQuery(client.me.getMyInfo(), {
    enabled: !isNil(token)
  })

  const myInfo = myInfoRequest.data

  useEffect(() => {
    if (!isNil(me) || !isNil(myInfo) || !isNil(org)) {
      const analyticsInstance = getAnalytics()
      if (!isNil(me)) {
        const isStaff = loginAs != null || canUser(ops.view, 'debugMode', { account: me })
        setUserProperties(analyticsInstance, { isStaff: `${isStaff}` })
      }
      if (!isNil(myInfo)) {
        if (myInfo.subscriptions.expert > 0 || myInfo.subscriptions.plus > 0) {
          setUserProperties(analyticsInstance, { premium: 'true' })
        } else {
          setUserProperties(analyticsInstance, { freemium: 'true' })
        }
        if (myInfo.subscriptions.expert > 0) {
          setUserProperties(analyticsInstance, { plan: 'expert' })
        } else if (myInfo.subscriptions.plus > 0) {
          setUserProperties(analyticsInstance, { plan: 'plus' })
        } else {
          setUserProperties(analyticsInstance, { plan: 'essential' })
        }
      }
      if (!isNil(org)) {
        const networkId = !isNil(org.network)
          ? org.network?.id
          : !isNil(org.networkType)
            ? org.id
            : null
        if (!isNil(networkId)) {
          setUserProperties(analyticsInstance, { subscribed_network: networkId })
        }
        setUserProperties(analyticsInstance, { organization_id: org.id })
      }
    }
  }, [loginAs, me, myInfo, org])
}

export type AddModalSelectParams = SelectableParams<
  'select',
  | 'create_plot'
  | 'add_device'
  | 'create_alert'
  | 'invite_member'
  | 'add_option'
  | 'invite_network_member'
>

export const logRegistered = () => {
  logEvent('registered')
  logEventFb('CompleteRegistration', undefined, 'track')
}

export const logNavigateOnRegistration = () => {
  logAll('register_page_view')
}

export const logSwitchUniverse = (params: SwitchUniverseParams) => {
  logEvent(params.action, params)
}
export const logSwitchUnivAgg = (params: SwitchUnivAggParams) => {
  logEvent(params.action, params)
}
export const logSwitchUnivMetric = (params: SwitchUnivMetricParams) => {
  logEvent(params.action, params)
}

const WEEDGET_EVENT_PREFIX = 'widget'

export const logWeedgetEvents = ({
  action,
  direction,
  widgetKey,
  logCategory = false,
  universe
}: {
  action: 'add' | 'rm' | 'mv'
  direction?: 'up' | 'down'
  widgetKey: AddableWeedgetKey
  logCategory?: boolean
  universe: Universe
}) => {
  const shorterKey = widgetKey
    .replace('cumulativeSum', 'cumSum')
    .replace('accumulation', 'acc')
    .replace('virtual_weather_station_with_wind', 'vws')
    .replace('virtual_weather_station', 'vws')

  logEvent(`${WEEDGET_EVENT_PREFIX}_${action}`)
  logEvent(`${WEEDGET_EVENT_PREFIX}_${action}${direction ? `_${direction}` : ''}_${shorterKey}`)
  if (logCategory) {
    logEvent(`${WEEDGET_EVENT_PREFIX}_${action}_${WEEDGET_CATEGORY[widgetKey]}`)
  }
  logEvent(`${WEEDGET_EVENT_PREFIX}_${action}_${Universe[universe]}`)
}

export default logEvent
